import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { leadAPI, crmUserAPI, Lead, CRMUser } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const LeadForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [users, setUsers] = useState<CRMUser[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    business_name: '',
    business_type: 'FEED_STORE',
    website: '',
    address: '',
    city: '',
    state: 'WA',
    zip_code: '',
    region: '',
    distance_from_kettle_falls: '',
    contact_name: '',
    contact_title: '',
    phone: '',
    email: '',
    preferred_contact_method: 'PHONE',
    status: 'NEW',
    source: 'RESEARCH',
    assigned_to_id: '',
    estimated_monthly_volume: '',
    interested_products: '',
    notes: '',
    next_follow_up: '',
  });

  useEffect(() => {
    loadUsers();
    if (isEditing) {
      loadLead();
    }
  }, [id]);

  const loadUsers = async () => {
    try {
      const response = await crmUserAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadLead = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await leadAPI.getById(parseInt(id));
      const lead = response.data;
      setForm({
        business_name: lead.business_name || '',
        business_type: lead.business_type || 'FEED_STORE',
        website: lead.website || '',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || 'WA',
        zip_code: lead.zip_code || '',
        region: lead.region || '',
        distance_from_kettle_falls: lead.distance_from_kettle_falls?.toString() || '',
        contact_name: lead.contact_name || '',
        contact_title: lead.contact_title || '',
        phone: lead.phone || '',
        email: lead.email || '',
        preferred_contact_method: lead.preferred_contact_method || 'PHONE',
        status: lead.status || 'NEW',
        source: lead.source || 'RESEARCH',
        assigned_to_id: lead.assigned_to_id?.toString() || '',
        estimated_monthly_volume: lead.estimated_monthly_volume || '',
        interested_products: lead.interested_products || '',
        notes: lead.notes || '',
        next_follow_up: lead.next_follow_up || '',
      });
    } catch (err) {
      setError('Error loading lead');
      console.error('Error loading lead:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const data: Partial<Lead> = {
        ...form,
        distance_from_kettle_falls: form.distance_from_kettle_falls ? parseInt(form.distance_from_kettle_falls) : undefined,
        assigned_to_id: form.assigned_to_id ? parseInt(form.assigned_to_id) : undefined,
      };

      if (isEditing && id) {
        await leadAPI.update(parseInt(id), data);
        navigate(`/crm/leads/${id}`);
      } else {
        const response = await leadAPI.create(data);
        navigate(`/crm/leads/${response.data.id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error saving lead');
      console.error('Error saving lead:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <div>
          <Link to="/crm/leads" className="back-link">← Back to Leads</Link>
          <h1>{isEditing ? 'Edit Lead' : 'Add New Lead'}</h1>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="lead-form">
        {/* Business Information */}
        <div className="form-section">
          <h3>Business Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Business Name *</label>
              <input
                type="text"
                name="business_name"
                value={form.business_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Business Type</label>
              <select name="business_type" value={form.business_type} onChange={handleChange}>
                <option value="FEED_STORE">Feed Store</option>
                <option value="FARM_SUPPLY">Farm Supply</option>
                <option value="CO_OP">Co-op</option>
                <option value="RANCH">Ranch/Farm</option>
                <option value="PET_STORE">Pet Store</option>
                <option value="GARDEN_CENTER">Garden Center</option>
                <option value="HARDWARE_STORE">Hardware Store</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <h3>Location</h3>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <select name="state" value={form.state} onChange={handleChange}>
                <option value="WA">Washington</option>
                <option value="ID">Idaho</option>
                <option value="MT">Montana</option>
                <option value="OR">Oregon</option>
              </select>
            </div>
            <div className="form-group">
              <label>ZIP Code</label>
              <input
                type="text"
                name="zip_code"
                value={form.zip_code}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Region</label>
              <input
                type="text"
                name="region"
                value={form.region}
                onChange={handleChange}
                placeholder="e.g., Northeast WA, Spokane Area"
              />
            </div>
            <div className="form-group">
              <label>Distance from Kettle Falls (miles)</label>
              <input
                type="text"
                inputMode="numeric"
                name="distance_from_kettle_falls"
                value={form.distance_from_kettle_falls}
                onChange={handleChange}
                onBlur={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 0) {
                    setForm(prev => ({ ...prev, distance_from_kettle_falls: String(val) }));
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Contact Name *</label>
              <input
                type="text"
                name="contact_name"
                value={form.contact_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="contact_title"
                value={form.contact_title}
                onChange={handleChange}
                placeholder="Owner, Manager, etc."
              />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Preferred Contact Method</label>
              <select name="preferred_contact_method" value={form.preferred_contact_method} onChange={handleChange}>
                <option value="PHONE">Phone</option>
                <option value="EMAIL">Email</option>
                <option value="TEXT">Text</option>
                <option value="IN_PERSON">In Person</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sales Information */}
        <div className="form-section">
          <h3>Sales Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={form.status} onChange={handleChange}>
                <option value="NEW">New Lead</option>
                <option value="RESEARCHING">Researching</option>
                <option value="CONTACTED">Contacted</option>
                <option value="MEETING_SCHEDULED">Meeting Scheduled</option>
                <option value="SAMPLED">Sent Samples</option>
                <option value="NEGOTIATING">Negotiating</option>
                <option value="WON">Won</option>
                <option value="LOST">Lost</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
            </div>
            <div className="form-group">
              <label>Source</label>
              <select name="source" value={form.source} onChange={handleChange}>
                <option value="WEBSITE">Website</option>
                <option value="REFERRAL">Referral</option>
                <option value="COLD_CALL">Cold Call</option>
                <option value="TRADE_SHOW">Trade Show</option>
                <option value="RESEARCH">Research</option>
                <option value="WALK_IN">Walk-in</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select name="assigned_to_id" value={form.assigned_to_id} onChange={handleChange}>
                <option value="">Unassigned</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Next Follow-up</label>
              <input
                type="date"
                name="next_follow_up"
                value={form.next_follow_up}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Est. Monthly Volume</label>
              <input
                type="text"
                name="estimated_monthly_volume"
                value={form.estimated_monthly_volume}
                onChange={handleChange}
                placeholder="e.g., 500-1000 lbs"
              />
            </div>
            <div className="form-group full-width">
              <label>Interested Products</label>
              <input
                type="text"
                name="interested_products"
                value={form.interested_products}
                onChange={handleChange}
                placeholder="e.g., Layer Feed, Scratch Grains, Horse Feed"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <h3>Notes</h3>
          <div className="form-group full-width">
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={5}
              placeholder="Additional notes about this lead..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <Link to="/crm/leads" className="btn btn-outline">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : (isEditing ? 'Update Lead' : 'Create Lead')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
